import { useCallback, useEffect, useState } from "react";
import { API_ROUTES, LIMITS, SKIN_CATALOG, type AdminWalletRow } from "@ziolo/shared";
import { Button, Card } from "@ziolo/ui";
import { apiRequest, errorMessage } from "../../lib/apiClient.js";
import { useFormSubmit } from "../../hooks/useFormSubmit.js";
import { confirmAdmin } from "../../hooks/useIsAdmin.js";
import { AdminPanel } from "./AdminChrome.js";
import "../../styles/admin.css";

export function CasinoOnPageAdmin({
  onChanged,
}: {
  onChanged?: (() => void | Promise<void>) | undefined;
}) {
  return (
    <AdminPanel title="Kasyno">
      <CasinoAdminTools onChanged={onChanged} />
    </AdminPanel>
  );
}

export function CasinoAdminTools({
  onChanged,
}: {
  onChanged?: (() => void | Promise<void>) | undefined;
}) {
  const { busy, submit } = useFormSubmit();
  const [wallets, setWallets] = useState<AdminWalletRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<number, { chips: string; keys: string }>>({});
  const [skins, setSkins] = useState<Record<number, string>>({});

  const reload = useCallback(async () => {
    const list = await apiRequest<AdminWalletRow[]>(API_ROUTES.admin.wallets);
    setWallets(list);
    setDrafts(
      Object.fromEntries(
        list.map((row) => [row.userId, { chips: String(row.chips), keys: String(row.keys) }]),
      ),
    );
    setError(null);
  }, []);

  useEffect(() => {
    reload().catch((cause: unknown) => setError(errorMessage(cause)));
  }, [reload]);

  async function saveWallet(row: AdminWalletRow) {
    const draft = drafts[row.userId];
    if (!draft) return;
    const chips = Number.parseInt(draft.chips, 10);
    const keys = Number.parseInt(draft.keys, 10);
    if (!Number.isInteger(chips) || !Number.isInteger(keys)) return;
    await submit(async () => {
      const updated = await apiRequest<AdminWalletRow>(API_ROUTES.admin.wallet(row.userId), {
        method: "PATCH",
        body: { chips, keys },
      });
      setWallets((current) =>
        current?.map((item) => (item.userId === updated.userId ? updated : item)) ?? null,
      );
      setDrafts((current) => ({
        ...current,
        [updated.userId]: { chips: String(updated.chips), keys: String(updated.keys) },
      }));
      await onChanged?.();
    }, "Zapisano portfel");
  }

  async function grantSkin(userId: number) {
    const skinId = skins[userId];
    if (!skinId) return;
    await submit(async () => {
      await apiRequest(API_ROUTES.admin.grantSkin(userId), {
        method: "POST",
        body: { skinId },
      });
      await onChanged?.();
    }, "Przyznano skina");
  }

  async function resetBoard() {
    if (!confirmAdmin("Wyzerować rekordy żetonów do aktualnego stanu portfeli?")) return;
    await submit(async () => {
      await apiRequest(API_ROUTES.admin.resetLeaderboard, { method: "POST" });
      await reload();
      await onChanged?.();
    }, "Zresetowano ranking kasyna");
  }

  if (error) return <p className="hub-error">{error}</p>;
  if (!wallets) return <p className="hub-hint">Wczytywanie portfeli...</p>;

  return (
    <Card title="Kasyno i sklep" icon="🎰">
      <p className="admin-muted">
        Żetony i klucze są wspólne dla blackjacka, ruletki, pokera, koni i sklepu.
      </p>
      <div className="admin-toolbar">
        <Button size="sm" variant="danger" disabled={busy} onClick={() => void resetBoard()}>
          Reset rankingu żetonów
        </Button>
      </div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Gracz</th>
              <th>Żetony</th>
              <th>Klucze</th>
              <th>Rekord</th>
              <th>Skin</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {wallets.map((row) => {
              const draft = drafts[row.userId] ?? { chips: "0", keys: "0" };
              return (
                <tr key={row.userId}>
                  <td>
                    <strong>{row.username}</strong>
                  </td>
                  <td>
                    <input
                      className="z-input"
                      type="number"
                      min={LIMITS.adminChips.min}
                      max={LIMITS.adminChips.max}
                      value={draft.chips}
                      onChange={(event) =>
                        setDrafts((current) => ({
                          ...current,
                          [row.userId]: { ...draft, chips: event.target.value },
                        }))
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="z-input"
                      type="number"
                      min={LIMITS.adminKeys.min}
                      max={LIMITS.adminKeys.max}
                      value={draft.keys}
                      onChange={(event) =>
                        setDrafts((current) => ({
                          ...current,
                          [row.userId]: { ...draft, keys: event.target.value },
                        }))
                      }
                    />
                  </td>
                  <td>{row.maxChips}</td>
                  <td>
                    <select
                      className="z-select"
                      value={skins[row.userId] ?? ""}
                      onChange={(event) =>
                        setSkins((current) => ({ ...current, [row.userId]: event.target.value }))
                      }
                    >
                      <option value="">Wybierz skina</option>
                      {SKIN_CATALOG.map((skin) => (
                        <option key={skin.id} value={skin.id}>
                          {skin.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className="admin-row-actions">
                      <Button size="sm" disabled={busy} onClick={() => void saveWallet(row)}>
                        Zapisz
                      </Button>
                      <Button
                        size="sm"
                        disabled={busy || !skins[row.userId]}
                        onClick={() => void grantSkin(row.userId)}
                      >
                        Daj skina
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
